import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, Check, Loader2 } from "lucide-react";

async function getCroppedBlob(src, area) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.92));
}

export default function ImageCropper({ src, aspect = 16 / 9, label, onCancel, onDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, px) => setAreaPixels(px), []);

  const apply = async () => {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, areaPixels);
      onDone(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-[#0A0D0B]/80 backdrop-blur-sm" data-testid="crop-modal">
      <div className="bg-white w-full max-w-2xl border border-[#E5E1D8]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1D8]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B89874]">Position &amp; crop</div>
            <h3 className="font-display text-xl text-[#0A0D0B]">{label}</h3>
          </div>
          <button onClick={onCancel} data-testid="crop-cancel" className="p-2 text-[#58635C] hover:text-[#0A0D0B]"><X size={20} /></button>
        </div>
        <div className="relative w-full h-[360px] bg-[#0A0D0B]">
          <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
        </div>
        <div className="px-6 py-4 flex items-center gap-4 border-t border-[#E5E1D8]">
          <span className="text-xs uppercase tracking-wide text-[#58635C]">Zoom</span>
          <input type="range" min={1} max={3} step={0.01} value={zoom} data-testid="crop-zoom"
            onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#B89874]" />
        </div>
        <div className="px-6 py-4 border-t border-[#E5E1D8] flex justify-end gap-3">
          <button onClick={onCancel} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
          <button onClick={apply} disabled={busy} data-testid="crop-apply"
            className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Use image
          </button>
        </div>
      </div>
    </div>
  );
}
